import React, { useEffect, useRef, useState } from 'react';

import {
  BaseButton,
  Input,
  VisaTypeList,
  CountryCodeList,
  useAuth,
  useT,
} from '@repo/common';

import { ReactComponent as ArrowBottomIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-bottom.svg';
import { ReactComponent as ArrowTopIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-top.svg';
import { ReactComponent as InfoIcon } from '@repo/common/assets/icons/20px/icon_20px_info_filled.svg';

const DEFAULT_COUNTRY = {
  name: 'South Korea',
  code: '+82',
  iso: 'KR',
};

const ApplyInfo = ({
  submitAttempted,
  onValidityChange,
  onChange,
  status,
  value,
  $isMobile,
}) => {
  const t = useT();
  const { user } = useAuth();

  const safeUser = user ?? {};
  const firstName = value?.firstName ?? safeUser.firstName ?? '';
  const lastName = value?.lastName ?? safeUser.lastName ?? '';
  const email = value?.email ?? safeUser.email ?? safeUser.socialEmail ?? '';
  const phoneCountry = value?.phoneCountry ?? safeUser.countryCode ?? '';
  const phone = value?.phone ?? '';
  const visaType = value?.visaType ?? safeUser.visaInfo?.type ?? '';

  const [isEdit, setIsEdit] = useState(false);
  const [isCountryOpen, setIsCountryOpen] = useState(false);
  const [isVisaTypeActive, setIsVisaTypeActive] = useState(false);

  const [editVisaType, setEditVisaType] = useState(visaType);
  const [editFirstName, setEditFirstName] = useState(firstName);
  const [editLastName, setEditLastName] = useState(lastName);
  const [editEmail, setEditEmail] = useState(email);
  const [editCountry, setEditCountry] = useState(DEFAULT_COUNTRY);
  const [editPhone, setEditPhone] = useState(phone);

  const nameValid = !!(
    (editFirstName && editLastName) ||
    (firstName && lastName)
  );
  const emailValid = !!(editEmail || email);
  const phoneValid = !!editPhone?.trim() && !!editCountry?.code;
  const visaValid = !!editVisaType.trim();

  const isInfoInvalid = !nameValid || !emailValid || !phoneValid || !visaValid;

  const countryRef = useRef(null);
  const visaRef = useRef(null);

  const isInitialized = useRef(false);

  useEffect(() => {
    if (isInitialized.current) return;
    if (!value || !value.phone) return;

    setEditFirstName(firstName);
    setEditLastName(lastName);
    setEditEmail(email);
    setEditPhone(phone);
    setEditVisaType(visaType);

    setEditCountry({
      name: 'Selected',
      code: phoneCountry || '+82',
      iso: '',
    });

    isInitialized.current = true;
  }, [firstName, lastName, email, phone, visaType, phoneCountry, value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        isCountryOpen &&
        countryRef.current &&
        !countryRef.current.contains(e.target)
      ) {
        setIsCountryOpen(false);
      }

      if (
        isVisaTypeActive &&
        visaRef.current &&
        !visaRef.current.contains(e.target)
      ) {
        setIsVisaTypeActive(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCountryOpen, isVisaTypeActive]);

  useEffect(() => {
    onValidityChange?.(isInfoInvalid);
  }, [isInfoInvalid, onValidityChange]);

  useEffect(() => {
    if (status === 'view') return;

    onChange?.({
      firstName: editFirstName || firstName,
      lastName: editLastName || lastName,
      phoneCountry: editCountry?.code,
      phone: editPhone,
      email: editEmail || email,
      visaType: editVisaType,
    });
  }, [
    editFirstName,
    editLastName,
    editCountry,
    editPhone,
    editEmail,
    editVisaType,
    status,
    onChange,
    firstName,
    lastName,
    email,
  ]);

  const InfoRow = ({ label, labelKr, value }) => {
    const fallbackByLabel = {
      phone: t('add your phone number', '전화번호를 추가해 주세요'),
      visa: t('add your visa type', '비자 유형을 추가해 주세요'),
    };

    const hasValue = value && value.trim?.();
    const displayValue = hasValue ? value : (fallbackByLabel[label] ?? '-');

    return (
      <div className='flex'>
        <div className='pr-2 min-w-[60px] max-w-[80px] typo-b13Strong tabletH:typo-b14Strong text-text-placeholder'>
          {t(label, labelKr)}
        </div>
        <div
          className={`typo-b13 tabletH:typo-b14 ${
            hasValue ? 'text-text-tertiary' : 'text-text-placeholder'
          }`}
        >
          {displayValue}
        </div>
      </div>
    );
  };

  return (
    <div className='tabletH:py-6 flex flex-col items-start'>
      <div className='flex flex-col w-full'>
        <div className='w-full flex justify-between items-center pb-2'>
          <div className='typo-b14Strong tabletH:typo-b16Strong text-text-primary'>
            {status === 'view'
              ? t('Contact information', '연락처 정보')
              : t('Check your contact info', '연락처 정보를 확인해 주세요.')}
          </div>

          {status === 'edit' && (
            <BaseButton
              type='text'
              size='s'
              hideIcon
              typo={false}
              isStatic
              textWrapperClass='h-auto'
              className={`
              h-auto min-w-0 cursor-pointer bg-transparent py-0
              px-1
              typo-b12Strong
              border-[1.6px] rounded-sm
              ${
                !isEdit
                  ? 'text-text-secondary hover:text-text-placeholder'
                  : 'text-text-accent hover:text-purple-purple04'
              }
              ${
                submitAttempted && isInfoInvalid
                  ? 'border-functional-failed02'
                  : 'border-transparent'
              }
            `}
              onClick={() => {
                setIsEdit((prev) => {
                  const next = !prev;
                  if (prev && !isInfoInvalid) {
                    onValidityChange?.(false);
                  }

                  return next;
                });
              }}
            >
              {isEdit ? t('Done', '완료') : t('Edit', '수정')}
            </BaseButton>
          )}
        </div>

        {submitAttempted && isInfoInvalid && status === 'edit' && (
          <div className='flex items-start pb-2 gap-1 text-text-failed typo-b14Strong'>
            <InfoIcon />
            <span className='mt-[1px]'>
              {t(
                'Please complete your personal information',
                '개인 정보를 모두 입력해 주세요',
              )}
            </span>
          </div>
        )}
      </div>

      {!isEdit ? (
        <div className='gap-2 grid grid-cols-1 tabletH:grid-cols-2 w-full'>
          <InfoRow
            label='name'
            labelKr='이름'
            value={`${editFirstName ? editFirstName : firstName} ${
              editLastName ? editLastName : lastName
            }`.trim()}
          />
          <InfoRow
            label='phone'
            labelKr='전화번호'
            value={
              editCountry?.code && editPhone
                ? `${editCountry ? editCountry.code : phoneCountry} ${
                    editPhone ? editPhone : phone
                  }`.trim()
                : ''
            }
          />
          <InfoRow label='email' labelKr='이메일' value={editEmail || email} />
          <InfoRow
            label='visa'
            labelKr='비자'
            value={editVisaType ? editVisaType : visaType}
          />
        </div>
      ) : (
        <div className='gap-4 grid grid-cols-1 tabletH:grid-cols-2 w-full'>
          <Input
            value={editFirstName}
            label={t('First name*', '이름*')}
            placeholder={t('As shown on your passport', '여권에 표기된 대로')}
            onChange={(e) => {
              setEditFirstName(e.target.value);
            }}
          />
          <Input
            value={editLastName}
            label={t('Last name*', '성*')}
            placeholder={t('As shown on your passport', '여권에 표기된 대로')}
            onChange={(e) => {
              setEditLastName(e.target.value);
            }}
          />
          <div className='relative' ref={countryRef}>
            <Input
              label={t('Country code*', '국가 코드*')}
              placeholder={t('Select', '선택')}
              value={
                editCountry ? `${editCountry.name} (${editCountry.code})` : ''
              }
              readOnly
              showStatusIcon={false}
              onClick={(e) => {
                e.stopPropagation();
                setIsCountryOpen((prev) => !prev);
              }}
              rightIcon={isCountryOpen ? <ArrowTopIcon /> : <ArrowBottomIcon />}
              onRightIconClick={(e) => {
                e.stopPropagation();
                setIsCountryOpen((prev) => !prev);
              }}
            />

            {isCountryOpen && (
              <div
                className='absolute top-full left-0 mt-1 w-full z-50'
                onMouseDown={(e) => e.stopPropagation()}
              >
                <CountryCodeList
                  selectedIso={editCountry?.iso}
                  onSelect={(country) => {
                    setEditCountry(country);
                    setIsCountryOpen(false);
                  }}
                />
              </div>
            )}
          </div>
          <Input
            value={editPhone}
            type='tel'
            label={t('Phone number*', '전화번호*')}
            placeholder='10-1234-5678'
            onChange={(e) => {
              setEditPhone(e.target.value);
            }}
          />
          <Input
            value={editEmail}
            label={t('Email*', '이메일*')}
            placeholder='your@email.com'
            onChange={(e) => {
              setEditEmail(e.target.value);
            }}
          />
          <div className='relative' ref={visaRef}>
            <Input
              label={t('Current Visa type*', '현재 비자 유형*')}
              placeholder={t('Select', '선택')}
              value={editVisaType}
              readOnly
              onClick={(e) => {
                e.stopPropagation();
                setIsVisaTypeActive((prev) => !prev);
              }}
              showStatusIcon={false}
              rightIcon={
                isVisaTypeActive ? <ArrowTopIcon /> : <ArrowBottomIcon />
              }
              onRightIconClick={(e) => {
                e.stopPropagation();
                setIsVisaTypeActive((prev) => !prev);
              }}
            />

            {isVisaTypeActive && (
              <div
                className='absolute top-full left-0 mt-1 w-full z-50'
                onClick={(e) => e.stopPropagation()}
              >
                <VisaTypeList
                  selectedOption={editVisaType}
                  onSelect={(value) => {
                    setEditVisaType(value);
                    setIsVisaTypeActive(false);
                  }}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplyInfo;
